import React from 'react';
import { Utensils, Users, Clock, TrendingUp } from 'lucide-react';
import { Button } from '../UI/Button';
import { useTranslation } from 'react-i18next';
import { getLanguagePathPrefix } from '../../i18n';
import { KombyLogo } from '../Branding/KombyLogo';
import { HERO_MEDIA } from '../../config/heroMedia';

const collage = HERO_MEDIA.resto.collage;

const StatPill: React.FC<{
  value: string;
  label?: string;
  className: string;
  delay?: string;
  from?: string;
  compact?: boolean;
}> = ({ value, label, className, delay = '0s', from = '-26px', compact = false }) => (
  <div
    className={`km-badge-in absolute z-20 ${className}`}
    style={{ animationDelay: delay, ['--km-from' as string]: from }}
  >
    <div
      className={`km-float rounded-2xl bg-komby-yellow text-center text-gray-950 shadow-lg ${compact ? 'rounded-xl px-1.5 py-1.5 min-[380px]:px-2.5' : 'px-4 py-3'}`}
      style={{ animationDelay: delay }}
    >
      <div className={`font-display font-bold leading-tight ${compact ? 'text-xs min-[380px]:text-sm' : 'text-2xl leading-none'}`}>{value}</div>
      {label ? <div className={`font-medium ${compact ? 'text-xs' : 'mt-1 text-sm'}`}>{label}</div> : null}
    </div>
  </div>
);

export const HeroResto: React.FC = () => {
  const { t } = useTranslation(['common', 'resto']);
  const interiorAlt = t('resto:images.restaurantInterior', {
    defaultValue: 'Restaurant interior',
  });

  return (
    <section className="relative overflow-hidden bg-komby-cream py-16 md:py-24">
      <div className="relative z-10 mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 items-center gap-12 lg:grid-cols-2 lg:gap-16">
          <div>
            <div className="mb-6 flex items-center gap-4">
              <div className="flex h-16 w-16 shrink-0 items-center justify-center rounded-xl bg-white p-3 shadow-sm">
                <Utensils className="h-9 w-9 text-yellow-600" />
              </div>
              <KombyLogo className="h-16 w-auto" animate={false} size={230} />
            </div>

            <h1 className="mb-6 font-display text-5xl font-bold leading-[1.02] tracking-tight text-gray-950 sm:text-6xl lg:text-7xl">
              {t('resto:seo.h1', { defaultValue: t('resto:hero.title') })}
            </h1>

            <p className="mb-4 text-lg font-medium text-gray-800 md:text-xl">
              {t('resto:hero.subtitle')}
            </p>

            <p className="mb-8 text-base leading-relaxed text-gray-700">
              {t('resto:hero.description')}
            </p>

            <div className="mb-8 grid grid-cols-2 gap-4">
              <div className="flex items-center space-x-3">
                <Utensils className="h-5 w-5 text-yellow-700" />
                <span className="text-gray-800">{t('resto:features.menus')}</span>
              </div>
              <div className="flex items-center space-x-3">
                <Users className="h-5 w-5 text-yellow-700" />
                <span className="text-gray-800">{t('resto:features.staffPlanning')}</span>
              </div>
              <div className="flex items-center space-x-3">
                <Clock className="h-5 w-5 text-yellow-700" />
                <span className="text-gray-800">{t('resto:features.reservations.title')}</span>
              </div>
              <div className="flex items-center space-x-3">
                <TrendingUp className="h-5 w-5 text-yellow-700" />
                <span className="text-gray-800">{t('resto:features.analytics')}</span>
              </div>
            </div>

            <div className="flex flex-col gap-4 sm:flex-row">
              <Button variant="primary" size="lg" onClick={() => {
                window.location.href = `${getLanguagePathPrefix()}/pricing`;
              }}>
                {t('resto:hero.ctaPrimary')}
              </Button>
              <Button variant="ghost" size="lg" className="border-2 border-gray-950 text-gray-950 hover:bg-gray-950 hover:!text-white dark:border-white dark:text-white dark:hover:bg-white dark:hover:!text-gray-950" onClick={() => {
                window.location.href = `${getLanguagePathPrefix()}/contact/`;
              }}>
                {t('resto:hero.ctaSecondary')}
              </Button>
            </div>
          </div>

          <div className="relative hidden h-[36rem] lg:block">
            <img
              src={collage.alley}
              alt=""
              className="absolute left-0 top-0 h-[68%] w-[30%] rounded-[1.75rem] object-cover shadow-lg"
              loading="lazy"
              decoding="async"
              width={480}
              height={720}
            />
            <img
              src={collage.terrace}
              alt={interiorAlt}
              className="absolute right-0 top-8 h-[70%] w-[66%] rounded-[1.75rem] object-cover object-[right_center] shadow-xl"
              loading="lazy"
              decoding="async"
              width={1024}
              height={683}
            />
            <img
              src={collage.table}
              alt=""
              className="absolute bottom-0 left-[16%] z-10 h-60 w-60 rounded-[1.75rem] object-cover shadow-2xl ring-4 ring-komby-cream"
              loading="lazy"
              decoding="async"
              width={640}
              height={640}
            />

            <StatPill
              className="left-3 top-4"
              value={t('resto:stats.restaurantsCount')}
              label={t('resto:stats.restaurants')}
            />
            <StatPill
              className="bottom-8 right-6"
              delay="0.28s"
              from="22px"
              value={t('resto:stats.satisfaction')}
            />
          </div>

          <div className="grid grid-cols-5 gap-3 lg:hidden">
            <div className="relative col-span-3">
              <img
                src={collage.terrace}
                srcSet={collage.terraceSrcSet}
                sizes="(min-width: 768px) 60vw, 40vw"
                alt={interiorAlt}
                className="h-64 w-full rounded-2xl object-cover object-[right_center]"
                loading="lazy"
                decoding="async"
                width={1024}
                height={683}
              />
              <StatPill
                className="bottom-2 left-2"
                compact
                value={t('resto:stats.restaurantsCount')}
                label={t('resto:stats.restaurants')}
              />
            </div>
            <div className="relative col-span-2">
              <img
                src={collage.table}
                srcSet={collage.tableSrcSet}
                sizes="40vw"
                alt=""
                className="h-64 w-full rounded-2xl object-cover"
                loading="lazy"
                decoding="async"
                width={640}
                height={640}
              />
              <StatPill
                className="bottom-2 inset-x-1.5 min-[380px]:inset-x-2"
                delay="0.28s"
                from="22px"
                compact
                value={t('resto:stats.satisfaction')}
              />
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};
